import React, {
    createRef,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from "react"
import { StyledComponent } from "@rogue_4/types"
import classNames from "classnames"

import { GlobalContext } from "@/topic/rogue_4/context"
import { ESubPage } from "@/topic/rogue_4/context"
import ModalContainer from "../components/Modal"
import SceneWrapper from "../components/SceneWrapper"
import { useGameStore } from "../store"
import { EGameScene, IDelayedAnimateRef } from "../type"
import CraftScene from "./craft"
import DialogScene from "./dialog"
import DialogBossScene from "./dialogBoss"
import EndGameScene from "./endGame"
import ResultScene from "./result"
import { useGameLayerStore, useNextScene } from "./store"
import TrailScene from "./trail"

import styles from "./index.module.less"

const GAME_SCENE_ORDER = [
    EGameScene.TRAIL,
    EGameScene.DIALOG,
    EGameScene.CRAFT,
    EGameScene.DIALOG_BOSS,
    EGameScene.RESULT,
    EGameScene.END_GAME,
]

const GameLayer: StyledComponent<{ onExit: () => void }> = ({
    className,
    style,
    onExit,
}) => {
    const setNextScene = useGameLayerStore((state) => state.setNextScene)
    const loadingRef = useRef<boolean>(false)

    const refDict = useMemo(
        () =>
            Object.values(EGameScene).reduce<
                Record<string, RefObject<IDelayedAnimateRef>>
            >((acc, k) => {
                acc[k] = createRef<IDelayedAnimateRef>()
                return acc
            }, {}),
        []
    )

    const { curScene, setCurScene } = useGameStore((state) => ({
        curScene: state.currentScene,
        setCurScene: state.setCurrentScene,
    }))
    const changeScene = useCallback(
        (scene: EGameScene) => {
            setCurScene(scene)
            Promise.all([
                curScene && refDict[curScene]?.current?.fadeOut(0),
                refDict[scene]?.current?.fadeIn(0),
            ]).then(() => {
                loadingRef.current = false
            })
        },
        [curScene, refDict, setCurScene]
    )
    useEffect(() => {
        const cb = () => {
            if (loadingRef.current) return false
            loadingRef.current = true
            const nextSceneIndex = curScene
                ? (GAME_SCENE_ORDER.indexOf(curScene) + 1) %
                  GAME_SCENE_ORDER.length
                : 0
            const nextScene = GAME_SCENE_ORDER[nextSceneIndex]
            changeScene(nextScene)

            return true
        }
        setNextScene(cb)
        const changeSceneCb = (value: EGameScene) => {
            if (loadingRef.current) return false
            loadingRef.current = true
            changeScene(value)
            return true
        }
        useGameLayerStore.setState({ changeScene: changeSceneCb })
    }, [changeScene, curScene, setCurScene, setNextScene])
    const {
        state: { currentSubPage },
    } = useContext(GlobalContext)
    useEffect(() => {
        if (currentSubPage === ESubPage.Game && !curScene) {
            changeScene(EGameScene.TRAIL)
        }
    }, [currentSubPage])

    return (
        <div className={classNames(styles.gameLayer, className)} style={style}>
            <SceneWrapper
                onExit={onExit}
                scene={TrailScene}
                ref={refDict[EGameScene.TRAIL]}
            />
            <SceneWrapper
                onExit={onExit}
                scene={DialogScene}
                ref={refDict[EGameScene.DIALOG]}
            />
            <SceneWrapper
                onExit={onExit}
                scene={CraftScene}
                ref={refDict[EGameScene.CRAFT]}
            />
            <SceneWrapper
                onExit={onExit}
                scene={DialogBossScene}
                ref={refDict[EGameScene.DIALOG_BOSS]}
            />
            <SceneWrapper
                onExit={onExit}
                scene={ResultScene}
                ref={refDict[EGameScene.RESULT]}
            />
            <SceneWrapper
                onExit={onExit}
                scene={EndGameScene}
                ref={refDict[EGameScene.END_GAME]}
            />
        </div>
    )
}

export default GameLayer
